import { getCurrencySymbol, NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, Input, ViewChild } from '@angular/core';
import {
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatFormField,
  MatLabel,
  MatError,
  MatSuffix,
  MatHint,
  MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RootState } from '@next/core-lib/app';
import { ChipsFilterComponent } from '@next/core-lib/components';
import { ChipItem } from '@next/core-lib/components';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  DialogService,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, skipWhile, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { FeedAdviceType } from 'src/app/feed-advice/models/feed-advice.model';
import { Product, ViewMode } from 'src/app/shared/models/shared.model';

import { EnergySystem, LysineSystem } from '../../../settings/models/settings.model';
import {
  getEnergySetting,
  getLysineSetting,
} from '../../../settings/store/selectors/settings.selectors';
import { ProductUpdateValidResponse } from '../../services/product-service.model';
import { ProductService } from '../../services/product.service';
import { getProductsRequest, setSelectedProductRequest } from '../../store/actions/product.actions';
import {
  getSelectedProduct,
  getSelectedProductCountry,
} from '../../store/selectors/product.selectors';

export interface ProductDetailDialogInputData extends AbstractFormDialogData {
  productId?: number;
  customerId: number;
  country: string;
  viewMode: ViewMode;
}
export interface ProductDetailDialogResultData {
  product: Product;
}

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    ChipsFilterComponent,
    AgrNumberDirective,
    MatSuffix,
    MatHint,
    MatPrefix,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProductDetailComponent extends AbstractFormDialog<ProductDetailDialogResultData> {
  @Input() formGroup: FormGroup;
  @ViewChild(ChipsFilterComponent) suitablePhasesComponent: ChipsFilterComponent<number>;

  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);
  lysineSetting$: Observable<LysineSystem> = this.store.select(getLysineSetting);

  pageTitle: string = this.localeService.translate('create-product.title-create-product');
  product: Product;
  currency: string;
  isViewModeView: boolean;
  isCustomerProducts: boolean;
  suitablePhasesOptions: ChipItem<number>[] = [
    {
      value: 1,
      name: '1',
    },
    {
      value: 2,
      name: '2',
    },
    {
      value: 3,
      name: '3',
    },
    {
      value: 4,
      name: '4',
    },
  ];

  feedAdviceTypesOptions: ChipItem<FeedAdviceType>[] = [
    {
      value: FeedAdviceType.FATTENING,
      name: this.localeService.translate('feedAdviceType.fattening'),
    },
    {
      value: FeedAdviceType.REARING,
      name: this.localeService.translate('feedAdviceType.rearing'),
    },
    {
      value: FeedAdviceType.WEANED_PIGLETS,
      name: this.localeService.translate('feedAdviceType.weanedpiglet-short'),
    },
    {
      value: FeedAdviceType.SOWS_BREEDTOFARROW,
      name: this.localeService.translate('feedAdviceType.sowsbreedtofarrow-short'),
    },
    {
      value: FeedAdviceType.SOWS_FARROWTOWEAN,
      name: this.localeService.translate('feedAdviceType.sowsfarrowtowean-short'),
    },
    {
      value: FeedAdviceType.SOWS_WEANTOBREED,
      name: this.localeService.translate('feedAdviceType.sowsweantobreed-short'),
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductDetailDialogInputData,
    protected mdDialogRef: MatDialogRef<
      ProductDetailComponent,
      AbstractDialogResultData<ProductDetailDialogResultData>
    >,
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
    public readonly productService: ProductService,
    private dialogService: DialogService,
  ) {
    super();

    this.store
      .select(RouterSelectors.getPath)
      .pipe(take(1))
      .subscribe((route: string) => {
        if (route.indexOf('/customer-product') !== -1) {
          this.isCustomerProducts = true;
        }
      });

    this.isViewModeView = data.viewMode === ViewMode.VIEW;

    this.data.title = this.localeService.translate(
      `create-product.title-${data.viewMode.toLocaleLowerCase()}-${
        this.data.customerId ? 'customer-' : ''
      }product`,
    );

    this.formGroup = new FormGroup({
      productName: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, [
        Validators.required,
      ]),
      productCode: new FormControl(
        { value: null, disabled: data.viewMode === ViewMode.VIEW },
        {
          validators: [Validators.required],
          asyncValidators: [this.validateProductcode()],
          updateOn: 'blur',
        },
      ),
      energy: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, [
        Validators.required,
      ]),
      lysine: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, [
        Validators.required,
      ]),
      suitablePhases: new FormControl([], {
        asyncValidators: [this.validatePhase()],
        updateOn: 'change',
      }),
      feedAdviceTypes: new FormControl([], {
        validators: [Validators.required],
        asyncValidators: [this.validateFeedAdviceType()],
        updateOn: 'change',
      }),
      grossPrice: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, []),
      phosphorus: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, []),
      protein: new FormControl({ value: null, disabled: data.viewMode === ViewMode.VIEW }, []),
    });

    this.store
      .select(getSelectedProductCountry)
      .pipe(take(1))
      .subscribe((country) => {
        this.store.dispatch(getProductsRequest({ country }));
      });

    const id = this.data.productId;
    if (id && id > 0) {
      this.store.dispatch(setSelectedProductRequest({ id }));
      this.store
        .select(getSelectedProduct)
        .pipe(
          skipWhile((c) => !c || c.id !== id),
          take(1),
        )
        .subscribe((product) => {
          this.product = product;

          this.pageTitle = `${this.localeService.translate('create-product.title-edit')} ${
            product.name
          }`;

          this.formGroup.controls.productName.setValue(product.name);
          this.formGroup.controls.productCode.setValue(product.code);
          this.formGroup.controls.energy.setValue(product.ewKg);
          this.formGroup.controls.lysine.setValue(product.lysKg);
          this.formGroup.controls.suitablePhases.setValue(product.suitablePhases);
          this.formGroup.controls.feedAdviceTypes.setValue(product.schemeTypes);
          this.formGroup.controls.grossPrice.setValue(product.grossPricePer100Kg);
          this.formGroup.controls.phosphorus.setValue(product.phosphorusGramPerKg);
          this.formGroup.controls.protein.setValue(product.proteinGramPerKg);

          this.currency = product.currency
            ? product.currency
            : getCurrencySymbol(this.localeService.getCurrency(), 'narrow');
        });
    }
  }

  phasesIsVisible() {
    const feedAdviceTypes = this.formGroup.controls.feedAdviceTypes.value as FeedAdviceType[];
    if (
      feedAdviceTypes?.some(
        (feedAdviceType) =>
          feedAdviceType === FeedAdviceType.FATTENING ||
          feedAdviceType === FeedAdviceType.REARING ||
          feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
      )
    ) {
      this.formGroup.get('suitablePhases').setValidators(Validators.required);
      return true;
    } else {
      this.formGroup.get('suitablePhases').clearValidators();
      return false;
    }
  }

  validateProductcode() {
    return <AsyncValidatorFn>((control: FormControl) => {
      if (this.product?.code !== control.value && !this.isCustomerProducts) {
        return this.energySetting$.pipe(
          withLatestFrom(this.lysineSetting$),
          skipWhile(([energy, lysine]) => energy === null || lysine === null),
          take(1),
          switchMap(([energySystem, lysineSystem]) =>
            this.productService
              .getProductByProductCode(energySystem, lysineSystem, control.value)
              .pipe(
                take(1),
                map((product) => {
                  if ((product as Product).id) {
                    return { productCodeAlreadyInUse: true };
                  }
                }),
              ),
          ),
        );
      }
      return of(null);
    });
  }

  validateFeedAdviceType() {
    return <AsyncValidatorFn>(() => {
      if (this.product) {
        return this.productService
          .checkUpdateValidityProduct({
            ...this.product,
            schemeTypes: this.formGroup?.controls.feedAdviceTypes.value,
          } as Product)
          .pipe(
            take(1),
            map((response) => {
              if (!(response as ProductUpdateValidResponse).id) {
                this.dialogService
                  .confirmWarning({
                    title: this.localeService.translate('create-product.validation-error.title'),
                    text: this.localeService.translate(
                      'create-product.validation-error.description',
                    ),
                    pushHistoryState: false,
                  })
                  .subscribe(() => {
                    this.formGroup?.controls.feedAdviceTypes.setValue(this.product?.schemeTypes);
                  });
              }
            }),
          ) as unknown;
      }
      return of(null);
    });
  }

  validatePhase() {
    return <AsyncValidatorFn>(() => {
      if (this.product) {
        return this.productService
          .checkUpdateValidityProduct({
            ...this.product,
            suitablePhases: this.formGroup?.controls.suitablePhases.value,
          } as Product)
          .pipe(
            take(1),
            map((response) => {
              if (!(response as ProductUpdateValidResponse).id) {
                this.dialogService
                  .confirmWarning({
                    title: this.localeService.translate('create-product.validation-error.title'),
                    text: this.localeService.translate(
                      'create-product.validation-error.description',
                    ),
                    pushHistoryState: false,
                  })
                  .subscribe(() => {
                    this.formGroup?.controls.suitablePhases.setValue(this.product?.suitablePhases);
                  });
              }
            }),
          ) as unknown;
      }
      return of(null);
    });
  }

  save() {
    if (this.formGroup.valid) {
      this.energySetting$
        .pipe(
          withLatestFrom(this.lysineSetting$),
          skipWhile(([energy, lysine]) => energy === null || lysine === null),
          take(1),
        )
        .subscribe(([energySystem, lysineSystem]) => {
          this.product = {
            ...this.product,
            name: <string>this.formGroup.controls.productName.value,
            suitablePhases: <number[]>this.formGroup.controls.suitablePhases.value,
            code: <string>this.formGroup.controls.productCode.value,
            ewKg: parseFloat(<string>this.formGroup.controls.energy.value),
            lysKg: parseFloat(<string>this.formGroup.controls.lysine.value),
            grossPricePer100Kg: parseFloat(<string>this.formGroup.controls.grossPrice.value),
            phosphorusGramPerKg: parseFloat(<string>this.formGroup.controls.phosphorus.value),
            proteinGramPerKg: parseFloat(<string>this.formGroup.controls.protein.value),
            currency: this.currency,
            customerId: this.data.customerId,
            country: this.data.country,
            energySystem,
            lysineSystem,
            schemeTypes: <FeedAdviceType[]>this.formGroup.controls.feedAdviceTypes.value,
          };
          this.confirm({
            product: this.product,
          });
        });
    }
  }
}
